/* Small primitives: Button, IconButton, Card, Chip, Toggle, Segmented, Skeleton, EmptyState, Banner,
   ProgressBar, AILabel, CapBadge, Kbd. Styles: web/src/styles/components.css. */
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode, Ref } from 'react';
import { useId } from 'react';
import { CircleAlert, Info, TriangleAlert, CircleCheck, type LucideIcon } from 'lucide-react';
import { cx } from './cx';

/* Button: one primary per header/card (progressive disclosure rule). */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  block?: boolean;
  ref?: Ref<HTMLButtonElement>;
}
export function Button({ variant = 'secondary', size = 'md', icon, block, className, children, type = 'button', ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      className={cx('btn', variant !== 'secondary' && variant, size !== 'md' && size, block && 'block', className)}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon: LucideIcon;
  size?: 'sm' | 'md';
  ref?: Ref<HTMLButtonElement>;
}
/** Icon-only button; `label` becomes the accessible name (and title). */
export function IconButton({ label, icon: Icon, size = 'md', className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button type={type} className={cx('icon-btn', size === 'sm' && 'sm', className)} aria-label={label} title={label} {...rest}>
      <Icon size={size === 'sm' ? 16 : 18} aria-hidden="true" />
    </button>
  );
}

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  as?: 'section' | 'div' | 'article';
}
export function Card({ title, icon, actions, as: Tag = 'section', className, children, ...rest }: CardProps) {
  return (
    <Tag className={cx('card', className)} {...rest}>
      {(title || actions) && (
        <div className="card-hd">
          {title && <h3>{icon}{title}</h3>}
          {actions && <div className="act">{actions}</div>}
        </div>
      )}
      {children}
    </Tag>
  );
}

/* Chip: state only (status, cost, AI label). */
export type ChipTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'agent' | 'brand';
export function Chip({ tone = 'neutral', icon, className, children, ...rest }: HTMLAttributes<HTMLSpanElement> & { tone?: ChipTone; icon?: ReactNode }) {
  return <span className={cx('chip', tone !== 'neutral' && tone, className)} {...rest}>{icon}{children}</span>;
}

export interface ToggleProps {
  label: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Full-width row with the switch on the right (settings lists, menus). */
  row?: boolean;
  description?: string;
}
export function Toggle({ label, checked, onChange, disabled, row }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={cx('toggle', row && 'row-toggle')}
      onClick={() => onChange(!checked)}
    >
      {row ? <><span>{label}</span><span className="track" aria-hidden="true" /></> : <><span className="track" aria-hidden="true" /><span>{label}</span></>}
    </button>
  );
}

export interface SegmentedOption<T extends string> { value: T; label: ReactNode; title?: string }
export function Segmented<T extends string>({ options, value, onChange, label, className }: { options: SegmentedOption<T>[]; value: T; onChange: (v: T) => void; label: string; className?: string }) {
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const next = options[(i + d + options.length) % options.length]!;
    onChange(next.value);
    const btns = (e.currentTarget.parentElement?.querySelectorAll('button') ?? []) as NodeListOf<HTMLButtonElement>;
    btns[(i + d + options.length) % options.length]?.focus();
  };
  return (
    <span className={cx('seg', className)} role="radiogroup" aria-label={label}>
      {options.map((o, i) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} tabIndex={o.value === value ? 0 : -1} title={o.title} onClick={() => onChange(o.value)} onKeyDown={(e) => onKey(e, i)}>
          {o.label}
        </button>
      ))}
    </span>
  );
}

export function Skeleton({ width, height = 16, radius, className, style }: { width?: number | string; height?: number | string; radius?: number; className?: string; style?: React.CSSProperties }) {
  return <span aria-hidden="true" className={cx('skel', className)} style={{ width: width ?? '100%', height, borderRadius: radius, ...style }} />;
}

export function EmptyState({ icon: Icon, title, purpose, action, example }: { icon?: LucideIcon; title: string; purpose: string; action?: ReactNode; example?: string }) {
  return (
    <div className="empty">
      {Icon && <Icon size={32} aria-hidden="true" />}
      <b>{title}</b>
      <p className="small muted">{purpose}</p>
      {action}
      {example && <small className="muted">Example: {example}</small>}
    </div>
  );
}

/* Banner (rule 41): what happened, what was kept, what to do; one recovery action. */
export type BannerTone = 'info' | 'warn' | 'danger' | 'ok';
const BANNER_ICON: Record<BannerTone, LucideIcon> = { info: Info, warn: TriangleAlert, danger: CircleAlert, ok: CircleCheck };
export function Banner({ tone = 'info', title, children, action, className }: { tone?: BannerTone; title: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  const Icon = BANNER_ICON[tone];
  return (
    <div className={cx('banner', tone, className)} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon size={18} aria-hidden="true" />
      <div className="banner-body"><b>{title}</b>{children && <div className="small">{children}</div>}</div>
      {action}
    </div>
  );
}

export function ProgressBar({ value, max = 100, label, valueText, tone, className }: { value: number; max?: number; label?: string; valueText?: string; tone?: 'warn' | 'danger' | 'agent'; className?: string }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const id = useId();
  return (
    <div className={className}>
      {label && <div className="pbar-label"><span id={id}>{label}</span><span>{valueText ?? `${Math.round(pct)}%`}</span></div>}
      <div className={cx('pbar', tone)} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-valuetext={valueText} aria-labelledby={label ? id : undefined}>
        <i style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Visible "AI" label (rule 37, spec 6.16). */
export function AILabel({ className }: { className?: string }) {
  return <span className={cx('ai-pill', className)} title="Made with AI by Crosspoint AI +" data-cap="SAFE-01">AI</span>;
}

/** Capability badge: always in the DOM with data-cap; visible only in the dev "IDs" mode. */
export function CapBadge({ id }: { id: string }) {
  return <span className="cap" data-cap={id} aria-hidden="true">{id}</span>;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd>{children}</kbd>;
}
